/**
 * Iter XX.272 — Gully Challenge #001 · Pulse discovery card.
 *
 * Renders at the top of the Pulse feed when the challenge is
 * exposable (both feature flag AND challenge.enabled must permit
 * exposure — the backend enforces this and returns available:false
 * otherwise). Participant count is REAL — zero is a valid state
 * and gets the empty-state copy per founder rule §2.
 *
 * All copy comes from persisted state. Never fabricates activity,
 * scarcity, or testimonials.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy } from "lucide-react";
import { trackFunnel } from "../lib/activationFunnel";

function _fmtStartDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric",
    });
  } catch {
    return null;
  }
}

export default function ChallengeCard({ state }) {
  useEffect(() => {
    if (!state || state.available === false) return;
    try {
      trackFunnel("challenge_impression", {
        challenge_id: state.challenge_id,
        user_status:  state.user_status,
        participant_count: state.participant_count,
        surface: "pulse",
      });
    } catch { /* fire-and-forget */ }
  }, [state?.challenge_id, state?.user_status]);

  if (!state || state.available === false) return null;

  const count = state.participant_count || 0;
  const isFirst = count === 0;
  const startCopy = _fmtStartDate(state.starts_at);

  const primaryCta = (() => {
    if (state.user_status === "participating") return "VIEW YOUR CHALLENGE";
    if (state.user_status === "completed")     return "VIEW YOUR SUMMARY";
    if (state.user_status === "invited")       return "YOU'RE INVITED";
    return "VIEW CHALLENGE";
  })();

  return (
    <div
      data-testid="challenge-card"
      className={
        "relative w-full rounded-2xl border border-emerald-900/50 "
        + "bg-gradient-to-br from-emerald-950/40 to-neutral-900/60 "
        + "p-5 mb-4"
      }
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-700/50 flex items-center justify-center">
          <Trophy size={16} className="text-emerald-300" aria-hidden="true" />
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.18em] text-emerald-400/90 font-semibold"
          data-testid="challenge-card-eyebrow"
        >
          Gully Challenge #001
        </div>
      </div>

      <div className="text-lg sm:text-xl font-semibold text-neutral-50 leading-tight mb-1">
        {state.headline || "7 DAYS. ONE THING."}
      </div>
      <p className="text-sm text-neutral-300 leading-relaxed mb-3">
        {state.description}
      </p>

      <div className="text-xs text-neutral-400 space-y-0.5 mb-4">
        {startCopy && (
          <div data-testid="challenge-card-start-date">
            Starts <span className="text-neutral-200">{startCopy}</span>
          </div>
        )}
        <div data-testid="challenge-card-participant-count">
          {isFirst
            ? "Be one of the first cricketers to join."
            : `${count} ${count === 1 ? "cricketer" : "cricketers"} joined`}
        </div>
      </div>

      <Link
        to={`/challenge/${state.challenge_id}`}
        data-testid="challenge-card-cta"
        onClick={() => {
          try {
            trackFunnel("challenge_detail_view", {
              challenge_id: state.challenge_id,
              source: "pulse_card",
            });
          } catch { /* noop */ }
        }}
        className={
          "inline-flex items-center gap-1.5 text-[11px] uppercase "
          + "tracking-[0.14em] text-emerald-300 font-semibold "
          + "hover:text-emerald-200 transition-colors"
        }
      >
        {primaryCta}
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}
